import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays } from "date-fns";
import type { MeetingsResponse } from "~/app/calendar/meetings/types/common";

import { axios, getBackendApiErrorResponse } from "~/core/axios";
import { REACT_QUERY_KEY } from "~/core/constants/common";
import type { BackendApiResponse } from "~/core/types/backend-api-response.type";

import { useListenGmailCalendarEvents } from "~/lib/gmail/hooks";

export const useMeetingsForNext24Hours = () => {
  const queryClient = useQueryClient();

  const { data: meetingsData, isLoading: loadingMeetingsData } = useQuery({
    queryKey: [REACT_QUERY_KEY.MEETINGS_FOR_NEXT_24_HOURS],
    queryFn: async () => {
      const start_date = new Date().toISOString();
      const end_date = addDays(new Date(), 1).toISOString();

      try {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", start_date);
        searchParams.set("end_date", end_date);

        const { data } = await axios.get<BackendApiResponse<MeetingsResponse>>(
          `/mail/user-meetings?${searchParams.toString()}`,
        );

        return data.data?.meetings || [];
      } catch (error) {
        const err = getBackendApiErrorResponse(error);
        console.error(err.data);
        return [];
      }
    },
    retry: 1,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  useListenGmailCalendarEvents(data => {
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEY.MEETINGS_FOR_NEXT_24_HOURS],
    });
  });

  return { meetingsData, loadingMeetingsData };
};
